/**
 * Files the user gives the wall (the + menu's Add files…, a drop on empty
 * wall space; a paste stays images only): each becomes the card its kind calls
 * for, placed by the backend. Images become image frames; markdown and
 * plain text editable Docs (while Docs are off markdown frames and notes),
 * titled from the file name; PDFs and anything else file frames. In the desktop app those
 * link the file where it is (plan 14: nothing is uploaded, agents read it
 * from its path); on the web, which has no path, they hold the upload.
 */
import { createFrame, updateFrame } from "@/api/spaces";
import { uploadImage } from "@/api/uploads";
import { newTextFrameFields } from "@/components/shapes/doc-frame/new-text-frame";
import { uploadFrameImage } from "@/components/shapes/shared/frame-image-upload";
import { capturePdfThumbnail } from "@/components/shapes/file-frame/pdf-thumbnail";
import {
  bindLinkedFile,
  linkGrantedFiles,
  thisComputer,
} from "@/desktop/local-files";
import {
  baseName,
  grantErrorReason,
  readGrantedFiles,
  reportUnopenedFiles,
  type UnreadFile,
} from "@/desktop/pick-native-files";
import { translate } from "@/i18n";
import { wallMessages } from "@/i18n/messages/wall";
import {
  fileExtension,
  fileKind,
  fileStem,
  maxFileBytes,
} from "@/lib/file-kinds";
import { fileSizeLabel } from "@/lib/file-size-label";
import { fileToast as toast } from "@/lib/file-toast";
import { getFrameDefaultSize } from "@/lib/frame-defaults";
import { linkedFileContent } from "@/lib/local-file-link";

/** Files handled at once: each holds its bytes (a PDF also pdf.js) in memory. */
const AT_ONCE = 3;
/**
 * A larger text file stays a file card: its Doc keeps the markdown and a
 * plain-text copy, which must fit the backend's 2 MB content cap.
 */
const MAX_TEXT_DOC_BYTES = 900 * 1024;

function toastTooLarge(name: string, max: number): void {
  toast(
    "error",
    translate(wallMessages, "fileTooLarge", {
      name,
      max: fileSizeLabel(max),
    }),
  );
}

/**
 * The files of a drop, in order, and the folders left out (a browser hands
 * them over as empty, unreadable files).
 */
export function droppedFiles(data: DataTransfer | null): {
  files: File[];
  skipped: UnreadFile[];
} {
  const files: File[] = [];
  const skipped: UnreadFile[] = [];
  for (const item of Array.from(data?.items ?? [])) {
    if (item.kind !== "file") continue;
    const file = item.getAsFile();
    if (item.webkitGetAsEntry?.()?.isDirectory) {
      skipped.push({ name: file?.name ?? "", reason: "folder" });
    } else if (file) {
      files.push(file);
    }
  }
  return { files, skipped };
}

/**
 * Says which dropped things were not added: each folder and oversized file
 * by name, the rest (unreadable) together.
 */
export function reportSkippedFiles(skipped: readonly UnreadFile[]): void {
  let unreadable = 0;
  for (const { name, reason } of skipped) {
    if (reason === "folder") {
      toast("info", translate(wallMessages, "folderSkipped", { name }));
    } else if (reason === "too-large") {
      toastTooLarge(name, maxFileBytes("file"));
    } else {
      unreadable += 1;
    }
  }
  reportUnopenedFiles(unreadable);
}

/**
 * Adds the files the desktop app was granted (a native pick or an OS drop)
 * to project `projectId`: images and text are read and added as files;
 * PDFs and every other file are linked where they are.
 */
export async function addPathsToWall(
  paths: readonly string[],
  projectId: string,
): Promise<void> {
  const read: string[] = [];
  const linked: string[] = [];
  for (const path of paths) {
    const kind = fileKind({ name: baseName(path), type: "" });
    (kind === "image" || kind === "doc" ? read : linked).push(path);
  }
  // Both at once: each grant lasts two minutes.
  await Promise.all([
    readGrantedFiles(read).then(({ files, unread }) => {
      reportSkippedFiles(unread);
      return addFilesToWall(files, projectId);
    }),
    linkFilesToWall(linked, projectId),
  ]);
}

/**
 * File frames that link each granted path where it is, each link bound to
 * its frame; a PDF's page 1 becomes its poster while the grant's file is
 * still open, the only part of it the server keeps.
 */
async function linkFilesToWall(
  paths: readonly string[],
  projectId: string,
): Promise<void> {
  if (paths.length === 0) return;
  const failed = (path: string) =>
    toast(
      "error",
      translate(wallMessages, "fileUploadFailed", { name: baseName(path) }),
    );
  let computer;
  let outcomes;
  try {
    [computer, outcomes] = await Promise.all([
      thisComputer(),
      linkGrantedFiles(paths),
    ]);
  } catch {
    paths.forEach(failed);
    return;
  }
  const skipped: UnreadFile[] = [];
  const size = getFrameDefaultSize("file");
  for (const { path, linked, error } of outcomes) {
    if (!linked) {
      const reason = grantErrorReason(error);
      if (reason === "unreadable") failed(path);
      else skipped.push({ name: baseName(path), reason });
      continue;
    }
    const content = linkedFileContent(linked, computer);
    try {
      const frame = await createFrame(projectId, {
        frame_type: "file",
        title: fileStem(linked.name),
        content,
        width: size.w,
        height: size.h,
      });
      await bindLinkedFile(linked.linkId, frame.id);
      if (content.kind === "pdf") {
        await capturePdfThumbnail(
          projectId,
          frame.id,
          { kind: "local", linkId: linked.linkId, frameId: frame.id },
          "",
        );
      }
    } catch {
      failed(path);
    }
  }
  reportSkippedFiles(skipped);
}

/** Adds each file to project `projectId`, {@link AT_ONCE} at a time. */
export async function addFilesToWall(
  files: readonly File[],
  projectId: string,
): Promise<void> {
  const queue = [...files];
  const work = async () => {
    for (let file = queue.shift(); file; file = queue.shift()) {
      await addFile(file, projectId);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(AT_ONCE, queue.length) }, work),
  );
}

async function addFile(file: File, projectId: string): Promise<void> {
  if (file.size === 0) {
    toast("info", translate(wallMessages, "fileEmpty", { name: file.name }));
    return;
  }
  const kind = fileKind(file);
  const max = maxFileBytes(kind);
  if (file.size > max) {
    toastTooLarge(file.name, max);
    return;
  }
  // The image path explains its own failures.
  if (kind === "image") {
    await uploadFrameImage(file, { projectId, spaceId: projectId });
    return;
  }
  try {
    if (kind === "doc" && (await addTextDoc(file, projectId))) return;
    await addFileCard(file, kind === "pdf", projectId);
  } catch (error) {
    toast(
      "error",
      translate(wallMessages, "fileUploadFailed", { name: file.name }),
      error instanceof Error ? error.message : undefined,
    );
  }
}

/**
 * A markdown or plain-text file as a Doc; false (a file card instead) when
 * it is not UTF-8 text, too long for one, or the Doc is refused.
 */
async function addTextDoc(file: File, projectId: string): Promise<boolean> {
  if (file.size > MAX_TEXT_DOC_BYTES) return false;
  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(
      await file.arrayBuffer(),
    );
  } catch {
    return false;
  }
  const source = fileExtension(file.name) === "txt" ? "plain" : "markdown";
  const size = getFrameDefaultSize("doc");
  try {
    await createFrame(projectId, {
      ...newTextFrameFields(text, source),
      title: fileStem(file.name),
      width: size.w,
      height: size.h,
    });
  } catch {
    // Refused (e.g. over the content cap): the file card holds it instead.
    return false;
  }
  return true;
}

/**
 * A file frame that shows the file while it uploads, then holds the
 * upload's URL; on failure it is marked as failed and the error is thrown.
 * The web's way; the desktop app links instead (`addPathsToWall`), except a
 * text file it read that could not become a Doc.
 */
async function addFileCard(
  file: File,
  pdf: boolean,
  projectId: string,
): Promise<void> {
  const content = {
    name: file.name,
    kind: pdf ? "pdf" : fileExtension(file.name),
    mimeType: file.type || "application/octet-stream",
    sizeBytes: file.size,
  };
  const size = getFrameDefaultSize("file");
  const frame = await createFrame(projectId, {
    frame_type: "file",
    title: fileStem(file.name),
    content,
    status: "streaming",
    width: size.w,
    height: size.h,
  });
  try {
    const uploaded = await uploadImage(file);
    await updateFrame(projectId, frame.id, {
      content: {
        ...content,
        url: uploaded.url,
        uploadId: uploaded.id,
        mimeType: uploaded.content_type,
        sizeBytes: uploaded.size_bytes,
      },
      status: "idle",
    });
  } catch (error) {
    await updateFrame(projectId, frame.id, { status: "error" }).catch(
      () => undefined,
    );
    throw error;
  }
}
